import { useFormikContext } from 'formik'
import { isBoolean, isString } from 'lodash'
import { ChangeEvent, ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import { INewPartnerData } from 'features/Partners/types'
import { SelectStates } from 'components/SelectStates'
import { Label } from 'components/v2/Label'
import { Checkbox, CheckboxWrapper } from 'components/v2/Checkbox'
import { GTBusinessPartnerType, GTBusinessPartnerVertical } from 'gql/types.generated'
import { safeFormatMessage } from '@10xlabs/utils'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { Radio, RadioWrapper } from 'components/v2/Radio'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { SelectState } from 'features/Cities/components/SelectState'
import { getStateByStateCode } from 'utils/states'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'

const VERTICALS_LIST = [
  GTBusinessPartnerVertical.Travel,
  GTBusinessPartnerVertical.City,
  GTBusinessPartnerVertical.Team,
  GTBusinessPartnerVertical.Rental
]

const PARTNER_TYPES_LIST = [GTBusinessPartnerType.Fleet, GTBusinessPartnerType.Host]

const partnerTypeMessages = defineMessages({
  [GTBusinessPartnerType.Host]: {
    id: 'partners.type.host',
    defaultMessage: 'Host'
  },
  [GTBusinessPartnerType.Fleet]: {
    id: 'partners.type.fleet',
    defaultMessage: 'Fleet'
  }
})

const messages = defineMessages({
  namePlaceholder: {
    id: 'partners.form.name.placeholder',
    defaultMessage: 'Enter the partner name'
  },
  cityNamePlaceholder: {
    id: 'partners.form.city.placeholder',
    defaultMessage: 'Select the city'
  }
})

export const PartnerDataFormCard = (): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, setValues, handleBlur, touched, errors } =
    useFormikContext<INewPartnerData>()

  const handlePartnerTypeChange = ({ target: { id: value } }: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue('type', value)
  }

  const handleVerticalsChange = (value: React.ChangeEvent<HTMLInputElement>): void => {
    const vertical = value.target.id as GTBusinessPartnerVertical
    const verticals = values.verticals.includes(vertical)
      ? values.verticals.filter((v: GTBusinessPartnerVertical): boolean => v !== vertical)
      : [...values.verticals, vertical]
    setFieldValue('verticals', verticals)
  }

  return (
    <FormLayout.Card small>
      <Heading.H4>
        <FormattedMessage id="partners.form.section.data.title" defaultMessage="Partner data" />
      </Heading.H4>
      <Row noMargin>
        <Column width="50%">
          <Input
            id="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            label={<FormattedMessage id="partners.form.name.label" defaultMessage="Partner name" />}
            touched={touched.name}
            errors={errors.name}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <SelectState
            id="state"
            label={<FormattedMessage id="partners.form.state.label" defaultMessage="Partner state" />}
            value={values.state?.code || ''}
            onChange={({ target: { value: stateCode } }: ChangeEvent<HTMLSelectElement>): void => {
              const state = getStateByStateCode(stateCode)
              setValues({
                ...values,
                state,
                city: values.city?.stateCode === stateCode ? values.city : undefined
              })
            }}
            touched={touched.state}
            errors={errors.state}
            optional
          />
        </Column>
        <Column width="50%">
          <CityAutocomplete
            id="city"
            label={<FormattedMessage id="partners.form.city.label" defaultMessage="Partner city" />}
            stateCode={values.state?.code}
            value={values.city?.name || ''}
            placeholder={intl.formatMessage(messages.cityNamePlaceholder)}
            onChange={(city): void => {
              setFieldValue('city', city)
            }}
            onBlur={handleBlur}
            touched={touched.city}
            errors={errors.city}
            optional
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Label optional>
            <FormattedMessage id="partners.form.operationalStates.label" defaultMessage="Operating states" />
          </Label>
          <SelectStates
            values={values.operationalStates}
            onChange={(states): void => {
              setFieldValue('operationalStates', states)
            }}
            touched={isBoolean(touched.operationalStates) ? touched.operationalStates : false}
            errors={isString(errors.operationalStates) ? errors.operationalStates : ''}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Label>
            <FormattedMessage id="partners.form.type.label" defaultMessage="Partner type" />
          </Label>
          <RadioWrapper>
            {PARTNER_TYPES_LIST.map((partnerType: GTBusinessPartnerType): ReactElement => {
              return (
                <Radio
                  id={partnerType}
                  key={partnerType}
                  name="partnerType"
                  checked={values.type === partnerType}
                  onChange={handlePartnerTypeChange}
                >
                  {safeFormatMessage({
                    intl,
                    key: partnerType,
                    messages: partnerTypeMessages
                  })}
                </Radio>
              )
            })}
          </RadioWrapper>
          {touched.type && errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
        </Column>
        <Column width="50%">
          <Label>
            <FormattedMessage id="partners.form.label" defaultMessage="Verticals" />
          </Label>
          <CheckboxWrapper>
            {VERTICALS_LIST.map((vertical: GTBusinessPartnerVertical): ReactElement => {
              return (
                <Checkbox
                  id={vertical}
                  key={vertical}
                  name="verticals"
                  checked={values.verticals.includes(vertical)}
                  onChange={handleVerticalsChange}
                >
                  {safeFormatMessage({
                    intl,
                    key: vertical,
                    messages: verticalMessages
                  })}
                </Checkbox>
              )
            })}
          </CheckboxWrapper>
          {touched.verticals && errors.verticals && <ErrorMessage>{errors.verticals}</ErrorMessage>}
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
